import {DataTable} from "../DataTable/DataTable";
import {TrailerEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {Anchor, Badge, Box, Modal, ModalProps} from "@mantine/core";
import {Link} from "react-router-dom";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useTrailerDataTableFilter} from "../TrailerDataTable/useTrailerDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";

const EMPTY_ARRAY: TrailerEntity[] = []

export type TrailerDataTableProps = EntityDataTableProps<TrailerEntity> & {
  showStatus?: boolean
}

export const TrailerDataTable = (props: TrailerDataTableProps) => {
  const {
    records: trailers = EMPTY_ARRAY,
    recordsPerPageOptions,
    showStatus = false,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useTrailerDataTableFilter({
    records: trailers
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<TrailerEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.trailer.table.column.name", "Name"),
        accessor: 'name',
        width: 100,
        sortable: true,
        render: (record) => (
          <EntityAnchor value={record} />
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "companyName"),
        title: t("common.entity.trailer.table.column.company", "Company"),
        accessor: 'companyName',
        width: 100,
        sortable: true,
        render: ({companyId, companyName}) => (
          companyId ? <DefaultText value={companyName}/> : undefined
        )
      },
      {
        ...getColumnFilterProps(filterConfig, "freightType"),
        title: t("common.entity.trailer.table.column.freighttype", "Freight Type"),
        accessor: 'freightTypeName',
        width: 100,
        sortable: true,
        render: ({freightTypeId, freightTypeName}) => (
          freightTypeId ? <DefaultText value={freightTypeName}/> : undefined
        )
      },
      {
        ...getColumnFilterProps(filterConfig, "barcode"),
        title: t("common.entity.trailer.table.column.qrcode", "QR Code"),
        accessor: 'barcode',
        width: 100,
        sortable: true,
      },
    ]

    if (showStatus) {
      values.push({
        title: '',
        accessor: "status",
        width: 50,
        textAlignment: 'right',
        render: ({state}) => {
          if (state && state.inShift) {
            return (
              <Badge color="green">Active</Badge>
            )
          }

          return undefined
        }
      })
    }

    return values
  }, [filterConfig, showStatus, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}

export type TrailerDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<TrailerDataTableProps, 'recordsPerPageOptions'>
}

export const TrailerDataTableModal = (props: TrailerDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.trailer.plural", "Trailers"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <TrailerDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
